import { useEffect } from "react"

import { ApiQueryName } from "@/models/api"
import type { Meter } from "@/models/meter"
import type { Organization } from "@/models/organization"
import { useGridUrlState } from "@/nzds/data-grid"
import { getBillPeriods } from "@/services/bill-period/get-bill-periods"
import { getDistinctMeterResourceCodes } from "@/services/bill/bill-resource"
import { useAllMeters } from "@/services/meter"
import { useQueryKey } from "@/services/utils"
import { parseModelId } from "@/utils/parse-model-id"
import { useQuery } from "@tanstack/react-query"
import { DateTime } from "luxon"

import type { GridSortModel } from "@mui/x-data-grid-pro"
import type { DateRange } from "@mui/x-date-pickers-pro/models"

import {
  defaultBillResourceCode,
  orderBillResourceCodes,
} from "./default-bill-resource"
import {
  SITE_BILLS_GRID_URL_KEYS,
  type SiteBillsGridUrlWrite,
  readSiteBillsGridUrl,
  toSiteBillsSortParam,
  writeSiteBillsGridUrl,
} from "./site-bills-grid-url"
import { openingBillDateRange } from "./site-bills-range"

interface UseSiteBillsSelectionOptions {
  organizationId: Organization["id"]
  siteId: string
}

const metersForResourceCode = (
  meters: readonly Meter[],
  resourceType: string | null
): Meter[] =>
  meters.filter(
    (meter) =>
      resourceType != null &&
      meter.meterSourceType?.resourceTypeCode?.trim() === resourceType
  )

const resolveResourceType = (
  addressResource: string | null,
  resourceCodes: readonly string[],
  metersAreComplete: boolean
): string | null => {
  if (addressResource && resourceCodes.includes(addressResource)) {
    return addressResource
  }

  if (!metersAreComplete) {
    return null
  }

  return defaultBillResourceCode(resourceCodes)
}

const readLatestStartDate = (
  startDate: string | null | undefined
): string | null => {
  const date = DateTime.fromISO(startDate?.trim() ?? "").startOf("day")
  return date.isValid ? date.toISODate() : null
}

interface SelectionUrlInput {
  end: string | null
  latestStartDate: string | null
  meterId: string | null
  meterIdsForResource: readonly string[]
  metersAreComplete: boolean
  probeIsSettled: boolean
  resource: string | null
  resourceCodes: readonly string[]
  start: string | null
  today: string
}

const buildSelectionUrlUpdates = ({
  end,
  latestStartDate,
  meterId,
  meterIdsForResource,
  metersAreComplete,
  probeIsSettled,
  resource,
  resourceCodes,
  start,
  today,
}: SelectionUrlInput): Record<string, string | null> => {
  const resourceType = resolveResourceType(
    resource,
    resourceCodes,
    metersAreComplete
  )
  const write: SiteBillsGridUrlWrite = {}

  if (metersAreComplete && resource !== resourceType) {
    write.resource = resourceType
  }

  const meterBelongs = meterId != null && meterIdsForResource.includes(meterId)
  if (
    metersAreComplete &&
    resourceType != null &&
    meterId != null &&
    !meterBelongs
  ) {
    write.meterId = null
  }

  if (probeIsSettled && resourceType != null && (!start || !end)) {
    const range = openingBillDateRange(latestStartDate, today)
    const rangeStart = range?.[0]?.toISODate()
    const rangeEnd = range?.[1]?.toISODate()
    if (rangeStart && rangeEnd) {
      write.end = rangeEnd
      write.start = rangeStart
    }
  }

  return writeSiteBillsGridUrl(write)
}

const getSortModelOrCurrent = (
  currentSortModel: GridSortModel,
  nextSortModel: GridSortModel
): GridSortModel =>
  nextSortModel.length > 0 ? nextSortModel : currentSortModel

export const useSiteBillsSelection = ({
  organizationId,
  siteId,
}: UseSiteBillsSelectionOptions) => {
  const siteIdNumber = parseModelId(siteId)
  const { replaceGridUrlState, values } = useGridUrlState(
    SITE_BILLS_GRID_URL_KEYS
  )
  const address = readSiteBillsGridUrl(values)
  const { metersData, metersIsComplete, metersIsError } = useAllMeters({
    apiQueryOptions: {
      includes: ["meter_source_type"],
      where: {
        organization_id: organizationId,
        site_id: siteIdNumber,
      },
    },
    enabled: siteIdNumber !== undefined,
  })
  const siteMeters = metersData ?? []
  const resourceCodes = orderBillResourceCodes(
    getDistinctMeterResourceCodes(siteMeters)
  )
  const resourceType = resolveResourceType(
    address.resource,
    resourceCodes,
    metersIsComplete
  )
  const resourceMeters = metersForResourceCode(siteMeters, resourceType)
  const latestQuery = useQuery({
    enabled:
      Boolean(organizationId) &&
      resourceType != null &&
      siteIdNumber !== undefined,
    queryFn: () => {
      if (resourceType == null || siteIdNumber === undefined) {
        throw new Error("resource and site are required")
      }

      return getBillPeriods({
        apiQueryOptions: {
          is_interval_derived: false,
          limit: 1,
          resource_type: resourceType,
          site_id: siteIdNumber,
          sort: "start_date:desc",
        },
        organizationId,
      })
    },
    queryKey: useQueryKey(ApiQueryName.BillPeriods, "getMany", null, {
      latestStart: true,
      organizationId,
      resourceType,
      siteId: siteIdNumber,
    }),
    retry: false,
  })
  const probeIsSettled =
    resourceType != null && !latestQuery.isFetching && latestQuery.isSuccess
  const probeFailed =
    resourceType != null && !latestQuery.isFetching && latestQuery.isError
  const latestStartDate = latestQuery.isSuccess
    ? readLatestStartDate(latestQuery.data.items[0]?.start_date)
    : null
  const today = DateTime.now().toISODate() ?? ""
  const addressEnd = address.end
  const addressMeterId = address.meterId
  const addressResource = address.resource
  const addressStart = address.start
  const meterIdsKey = resourceMeters
    .map((meter) => String(meter.id))
    .join("\u0000")
  const resourceCodesKey = resourceCodes.join("\u0000")

  useEffect(() => {
    const updates = buildSelectionUrlUpdates({
      end: addressEnd,
      latestStartDate,
      meterId: addressMeterId,
      meterIdsForResource:
        meterIdsKey === "" ? [] : meterIdsKey.split("\u0000"),
      metersAreComplete: metersIsComplete,
      probeIsSettled,
      resource: addressResource,
      resourceCodes:
        resourceCodesKey === "" ? [] : resourceCodesKey.split("\u0000"),
      start: addressStart,
      today,
    })
    if (Object.keys(updates).length === 0) {
      return
    }

    replaceGridUrlState(updates)
  }, [
    addressEnd,
    addressMeterId,
    addressResource,
    addressStart,
    latestStartDate,
    meterIdsKey,
    metersIsComplete,
    probeIsSettled,
    replaceGridUrlState,
    resourceCodesKey,
    today,
  ])

  const dateRange =
    address.start && address.end
      ? { end: address.end, start: address.start }
      : null
  const selectedMeter =
    resourceMeters.find((meter) => String(meter.id) === address.meterId) ?? null
  const anchorEnd =
    probeIsSettled && latestStartDate ? latestStartDate : address.end
  const metersFailed = metersIsError
  const selectionIsError = metersFailed || (probeFailed && dateRange == null)
  const isSelectionPending =
    !metersFailed &&
    !probeFailed &&
    (!metersIsComplete || (resourceCodes.length > 0 && dateRange == null))

  const handleDateRangeChange = (range: DateRange<DateTime>) => {
    const start = range[0]?.toISODate()
    const end = range[1]?.toISODate()
    if (!start || !end || start > end) {
      return
    }

    replaceGridUrlState(writeSiteBillsGridUrl({ end, start }))
  }

  const handleMeterChange = (meter: Meter | null) => {
    replaceGridUrlState(writeSiteBillsGridUrl({ meterId: meter?.id ?? null }))
  }

  const handleResourceChange = (resource: string) => {
    replaceGridUrlState(writeSiteBillsGridUrl({ meterId: null, resource }))
  }

  const handleSortModelChange = (nextSortModel: GridSortModel) => {
    replaceGridUrlState(
      writeSiteBillsGridUrl({
        sortModel: getSortModelOrCurrent(address.sortModel, nextSortModel),
      })
    )
  }

  return {
    anchorEnd,
    dateRange,
    handleDateRangeChange,
    handleMeterChange,
    handleResourceChange,
    handleSortModelChange,
    isSelectionPending,
    metersAreComplete: metersIsComplete,
    resourceCodes,
    resourceMeters,
    resourceType,
    selectedMeter,
    selectionIsError,
    siteIdNumber,
    sortModel: address.sortModel,
    sortParam: toSiteBillsSortParam(address.sortModel),
  }
}
